/** Shared types for the Alert Hexagon components — and the Map View, which speaks the
 *  same colour language so an operator reads one board the way they read the other. */
import type { ComponentType } from 'react';
import type { HexAlert } from '../../api/contracts';

export type HexColorState = 'offline' | 'grey' | 'blue' | 'amber' | 'red';

/** Per-severity alert palette + pulse tempo. Tempo encodes urgency: critical fast,
 *  warning medium, info slow. Shared by the hexagon and the map marker so the two
 *  surfaces can never drift apart on what "critical" looks like. */
export interface AlertFx {
  /** Brand severity colour — flat fill for the steady core and the pulsing ring. */
  core: string;
  /** Outer glow colour. */
  glow: string;
  /** Pulse period — faster = more urgent. */
  duration: string;
}

export const ALERT_FX: Partial<Record<HexColorState, AlertFx>> = {
  red: { core: '#e61b24', glow: 'rgba(230,27,36,0.7)', duration: '1s' },
  amber: { core: '#f59e0b', glow: 'rgba(245,158,11,0.65)', duration: '1.6s' },
  blue: { core: '#015ab5', glow: 'rgba(30,120,220,0.65)', duration: '2.4s' },
};

/** The board legend. Alerting entries pulse like the markers themselves, so the
 *  blinking on the canvas is self-explanatory. */
export const ALERT_LEGEND: { c: string; label: string; pulse?: boolean }[] = [
  { c: 'bg-emerald-400', label: 'Running' },
  { c: 'bg-red-500', label: 'Stopped' },
  { c: 'bg-red-600', label: 'Critical', pulse: true },
  { c: 'bg-amber-500', label: 'Warning', pulse: true },
  { c: 'bg-blue-600', label: 'Info', pulse: true },
];

/**
 * What a board hands its host when the operator opens an alert.
 *
 * `siblings` is the panel's whole active-alert list at the moment of the click, latest-first,
 * with `index` pointing at the one clicked — that is what lets the detail modal step to the next
 * alert with the arrow keys instead of being a dead end. It's a SNAPSHOT on purpose: the board's
 * feed keeps moving in real time, and a list that reordered under the reader's arrow key would
 * take them somewhere they didn't ask for.
 */
export interface ViewAlertTarget {
  /** Absent when the node broadcast the alert before its storage lane wrote the row. */
  id?: string;
  cameraId: string;
  /** Camera display name. */
  name: string;
  /** The alert's own name — what fired, for the detail's title. */
  alertName?: string;
}

export interface ViewAlertRequest extends ViewAlertTarget {
  siblings?: ViewAlertTarget[];
  index?: number;
}

/** One board alert reduced to what a detail view needs to title it and fetch it. */
export function viewAlertTarget(alert: HexAlert, nameById: Record<string, string>): ViewAlertTarget {
  return {
    id: alert.id,
    cameraId: alert.cameraId,
    name: nameById[alert.cameraId] || alert.cameraId,
    // The alert's own name, so its detail can be titled with WHAT fired rather than with the
    // generic "Alert Details" — the camera is already the subtitle.
    alertName: alert.alertName || alert.eventType,
  };
}

/**
 * The per-camera live video player is INJECTED by each app (local = SFUPlayer over a
 * shared SFU PeerConnection; cloud = LiveStreamPlayer over NodeConnectionManager).
 * Both satisfy this minimal prop shape.
 */
export type HexPlayerComponent = ComponentType<{
  cameraId: string;
  label?: string;
  className?: string;
  showOverlay?: boolean;
  /** Fired when the live video actually starts playing (first decoded frame), not when
   *  the subscription is merely requested. Used by the alert tile to start its auto-stop
   *  countdown only once the stream is visible (cold-start can be several seconds). May
   *  fire again after a stall/recovery; consumers should treat it idempotently. */
  onStreamStart?: () => void;
}>;
